import React, { Component } from 'react';
import { getPlatformSettings, updatePlatformSettings } from '../../api/adminApi';
import swal from 'sweetalert';

const DEFAULT_SETTINGS = {
  usd_to_npr_rate: '133.00',
  max_booking_months_ahead: '12',
  default_group_markup: '15',
  default_group_interest: '10',
  default_private_markup: '15',
  default_private_interest: '0',
  default_shared_markup: '15',
  default_shared_interest: '10',
};

const PRICING_ROWS = [
  {
    key: 'group',
    label: 'Group departures',
    markupField: 'default_group_markup',
    interestField: 'default_group_interest',
    note: 'CMS journey group bookings and vendor group tours when markup is left blank.',
  },
  {
    key: 'private',
    label: 'Private tours',
    markupField: 'default_private_markup',
    interestField: 'default_private_interest',
    note: 'CMS journey private previews and vendor private tours when markup is left blank.',
  },
  {
    key: 'shared',
    label: 'Shared rentals',
    markupField: 'default_shared_markup',
    interestField: 'default_shared_interest',
    note: 'Vendor shared rentals when markup is left blank.',
  },
];

function toInputString(value, fallback) {
  return value != null && value !== '' ? String(value) : fallback;
}

function parsePercent(value, label) {
  if (value === '' || value == null) return { ok: true, value: 0 };
  const num = parseFloat(value);
  if (Number.isNaN(num) || num < 0 || num > 100) {
    return { ok: false, message: `${label} must be between 0 and 100.` };
  }
  return { ok: true, value: num };
}

export default class AdminSettings extends Component {
  constructor(props) {
    super(props);
    this.state = {
      ...DEFAULT_SETTINGS,
      loading: true,
      saving: false,
    };
  }

  componentDidMount() {
    this.fetchSettings();
  }

  fetchSettings = () => {
    this.setState({ loading: true });
    getPlatformSettings()
      .then((res) => {
        const data = res.data || {};
        this.setState({
          usd_to_npr_rate: toInputString(data.usd_to_npr_rate, DEFAULT_SETTINGS.usd_to_npr_rate),
          max_booking_months_ahead: toInputString(data.max_booking_months_ahead, DEFAULT_SETTINGS.max_booking_months_ahead),
          default_group_markup: toInputString(data.default_group_markup, DEFAULT_SETTINGS.default_group_markup),
          default_group_interest: toInputString(data.default_group_interest, DEFAULT_SETTINGS.default_group_interest),
          default_private_markup: toInputString(data.default_private_markup, DEFAULT_SETTINGS.default_private_markup),
          default_private_interest: toInputString(data.default_private_interest, DEFAULT_SETTINGS.default_private_interest),
          default_shared_markup: toInputString(data.default_shared_markup, DEFAULT_SETTINGS.default_shared_markup),
          default_shared_interest: toInputString(data.default_shared_interest, DEFAULT_SETTINGS.default_shared_interest),
          loading: false,
        });
      })
      .catch(() => {
        this.setState({ ...DEFAULT_SETTINGS, loading: false });
      });
  };

  handleFieldChange = (field) => (e) => {
    this.setState({ [field]: e.target.value });
  };

  handleSave = () => {
    const {
      usd_to_npr_rate,
      max_booking_months_ahead,
      default_group_markup,
      default_group_interest,
      default_private_markup,
      default_private_interest,
      default_shared_markup,
      default_shared_interest,
    } = this.state;

    const rate = parseFloat(usd_to_npr_rate);
    if (Number.isNaN(rate) || rate <= 0) {
      swal('Invalid', 'Enter a positive USD to NPR rate (e.g. 133.00).', 'warning');
      return;
    }

    const horizon = parseInt(max_booking_months_ahead, 10);
    if (Number.isNaN(horizon) || horizon < 1 || horizon > 24) {
      swal('Invalid', 'Max booking months ahead must be between 1 and 24.', 'warning');
      return;
    }

    const checks = [
      parsePercent(default_group_markup, 'Group markup %'),
      parsePercent(default_group_interest, 'Group interest %'),
      parsePercent(default_private_markup, 'Private markup %'),
      parsePercent(default_private_interest, 'Private interest %'),
      parsePercent(default_shared_markup, 'Shared markup %'),
      parsePercent(default_shared_interest, 'Shared interest %'),
    ];
    const failed = checks.find((c) => !c.ok);
    if (failed) {
      swal('Invalid', failed.message, 'warning');
      return;
    }

    const payload = {
      usd_to_npr_rate: rate,
      max_booking_months_ahead: horizon,
      default_group_markup: checks[0].value,
      default_group_interest: checks[1].value,
      default_private_markup: checks[2].value,
      default_private_interest: checks[3].value,
      default_shared_markup: checks[4].value,
      default_shared_interest: checks[5].value,
    };

    this.setState({ saving: true });
    updatePlatformSettings(payload)
      .then((res) => {
        const data = res.data || {};
        this.setState({
          usd_to_npr_rate: toInputString(data.usd_to_npr_rate, String(rate)),
          max_booking_months_ahead: toInputString(data.max_booking_months_ahead, String(horizon)),
          default_group_markup: toInputString(data.default_group_markup, String(payload.default_group_markup)),
          default_group_interest: toInputString(data.default_group_interest, String(payload.default_group_interest)),
          default_private_markup: toInputString(data.default_private_markup, String(payload.default_private_markup)),
          default_private_interest: toInputString(data.default_private_interest, String(payload.default_private_interest)),
          default_shared_markup: toInputString(data.default_shared_markup, String(payload.default_shared_markup)),
          default_shared_interest: toInputString(data.default_shared_interest, String(payload.default_shared_interest)),
          saving: false,
        });
        swal('Saved', 'Platform settings updated.', 'success');
      })
      .catch((err) => {
        this.setState({ saving: false });
        const msg = (err.response?.data?.errors || [])?.join(' ') || 'Failed to save.';
        swal('Error', msg, 'error');
      });
  };

  renderPercentInput = (id, value, onChange) => (
    <input
      id={id}
      type="number"
      min="0"
      max="100"
      step="0.01"
      className="form-control"
      style={{ maxWidth: '120px' }}
      value={value}
      onChange={onChange}
    />
  );

  render() {
    const { loading, saving } = this.state;

    return (
      <div className="admin-settings">
        <h4 className="mb-4">Platform settings</h4>
        {loading ? (
          <p className="text-muted">Loading…</p>
        ) : (
          <>
            <div className="card mb-4">
              <div className="card-body">
                <h5 className="card-title">Pricing defaults</h5>
                <p className="text-muted small mb-4">
                  Applied when a listing or CMS journey has no per-listing markup/interest override.
                  Customer price = vendor base + markup; deposit = interest % of final price.
                </p>
                <div className="table-responsive">
                  <table className="table table-sm align-middle mb-0">
                    <thead>
                      <tr>
                        <th>Product</th>
                        <th style={{ width: '140px' }}>Markup %</th>
                        <th style={{ width: '140px' }}>Interest %</th>
                        <th>Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {PRICING_ROWS.map((row) => (
                        <tr key={row.key}>
                          <td className="font-weight-bold">{row.label}</td>
                          <td>
                            {this.renderPercentInput(
                              row.markupField,
                              this.state[row.markupField],
                              this.handleFieldChange(row.markupField)
                            )}
                          </td>
                          <td>
                            {this.renderPercentInput(
                              row.interestField,
                              this.state[row.interestField],
                              this.handleFieldChange(row.interestField)
                            )}
                          </td>
                          <td className="text-muted small">{row.note}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div className="card mb-4">
              <div className="card-body">
                <h5 className="card-title">Booking window</h5>
                <p className="text-muted small">
                  Customers cannot book group departures or private tours beyond this many months from today.
                  Per-journey seasonal months are set on each CMS journey.
                </p>
                <div className="form-group mb-0">
                  <label htmlFor="max_booking_months_ahead">Max months ahead</label>
                  <input
                    id="max_booking_months_ahead"
                    type="number"
                    min="1"
                    max="24"
                    step="1"
                    className="form-control w-auto d-inline-block ml-2"
                    style={{ maxWidth: '120px' }}
                    value={this.state.max_booking_months_ahead}
                    onChange={this.handleFieldChange('max_booking_months_ahead')}
                  />
                </div>
              </div>
            </div>

            <div className="card mb-4">
              <div className="card-body">
                <h5 className="card-title">USD to NPR conversion</h5>
                <p className="text-muted small">
                  Used when customers choose to pay in NPR for USD listings (or vice versa).
                  Existing orders are not affected.
                </p>
                <div className="form-group mb-0">
                  <label htmlFor="usd_to_npr_rate">1 USD =</label>
                  <input
                    id="usd_to_npr_rate"
                    type="number"
                    min="0.01"
                    step="0.01"
                    className="form-control w-auto d-inline-block ml-2"
                    style={{ maxWidth: '120px' }}
                    value={this.state.usd_to_npr_rate}
                    onChange={this.handleFieldChange('usd_to_npr_rate')}
                  />
                  <span className="ml-2">NPR</span>
                </div>
              </div>
            </div>

            <button
              type="button"
              className="btn btn-primary"
              onClick={this.handleSave}
              disabled={saving}
            >
              {saving ? 'Saving…' : 'Save settings'}
            </button>
          </>
        )}
      </div>
    );
  }
}
